import React, { FunctionComponent, useEffect, useState } from 'react'
import { Divider, useMediaQuery, useTheme } from '@material-ui/core'
import { Trans, useTranslation } from 'react-i18next'
import { FiSave } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import fireToastError from '../../../../actions/toasts/fireToastError'
import TemplateSendModalWrapper from '../../../../components/Calendar/ReservationModal/TemplateSendModalWrapper'
import { Select } from '../../../../components/FormBuilder'
import Button from '../../../../components/Template/Button'
import Flag from '../../../../components/Template/Flag'
import {
  ModalHeader,
  ModalHeaderLabel,
} from '../../../../components/Template/Modal'
import ModalFlex from '../../../../components/Template/Modal/ModalFlex'
import {
  asCountryCode,
  CountryCode,
  countryCodes,
} from '../../../../i18n/countryCodes'
import mapCodeToLanguage from '../../../../i18n/mappers/mapCodeToLanguage'
import { IEvent } from '../../../../models/Event'
import { ITemplate } from '../../../../models/Templates'
import store from '../../../../store'
import Icon from '../../../../components/Template/Modal/CloseButton/Icon'
import { isSafari } from '../../../../components/DetectBrowser/DetectBrowser'
import useSize from '../../../../hooks/useSize'
import mapCodeToTemplate from '../../../../i18n/mappers/mapCodeToTemplate'
import { is } from '../../../../components/MediaQuery'
import { Mixpanel } from '../../../../utils/Mixpanel'
import { useDispatch, useSelector } from 'react-redux'
import { getUserId, getUserPriceGroup, isUserNewPackage } from '../../../../reducers/userAccount/selectors/userAccount'
import { differenceInCalendarDays } from 'date-fns'
import {
  getModalCreateReservationIsOpen,
  getModalUpdateReservationIsOpen,
} from '../../../../reducers/bookingCalendar/selectors/getModalOpen'
import useIsScope from '../../../../hooks/useIsScope'
import { decodeEmojiFromBackend } from '../../../../utils/encodeEmojiForBackend'
import { PermissionScope } from '../../../Employees'
import { loadLanguage } from '../../../../i18n/languages'
import Popover from '@material-ui/core/Popover'
import { UpgradePremiumPopover } from '../../../../screens/UpgradePremium/UpgradePremium'
import {
  getActiveLicense, isNewPremiumLicenseValid,
} from '../../../../reducers/userAccount/selectors/license'
import { PopoveredIcon2 } from '../../../../components/PopoveredIcon/PopoveredIcon'
import { LicenseType } from '../../../../models/License'
import UpgradePremiumBlockScreenModal from '../../../UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'

export interface ISendEmailModalProps {
  open?: boolean
  email: string
  templates: ITemplate[]
  initialLanguage: CountryCode
  mapLabels: (string) => (CountryCode) => string
  event: IEvent
  type?: 'sms' | 'mailto'
  tab?: string
  employee?: boolean
}

export interface ISendEmailModalDispatch {
  onSend: (template: ITemplate) => void
  close?: () => void
}

export const TemplateSendModal: FunctionComponent<
  ISendEmailModalProps & ISendEmailModalDispatch
> = ({
       open,
       email,
       close,
       tab,
       mapLabels,
       templates,
       event,
       initialLanguage,
       type = 'mailto',
       employee = undefined,
     }) => {
  const mapLanguage = lang => (lang === 'pl' ? 'pl_PL' : lang)

  const [language, switchLanguage] = useState(mapLanguage(initialLanguage))
  const [template, switchTemplate] = useState(null as ITemplate)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [sendEnabled, setEnabled] = useState(false)
  const { t } = useTranslation()
  const issafari = isSafari()
  const size = useSize('forms')
  const userId = useSelector(getUserId)
  const updateOpen = useSelector(getModalCreateReservationIsOpen)
  const createOpen = useSelector(getModalUpdateReservationIsOpen)
  const [anchorEl, setAnchorEl] = useState(null)
  const userActiveLicense = useSelector(getActiveLicense)
  const userPriceGroup = +useSelector(getUserPriceGroup)
  const newPremium = useSelector(isNewPremiumLicenseValid)
  const isNewPremiumPriceGroup = useSelector(isUserNewPackage)
  const isNewPremiumUser = newPremium && isNewPremiumPriceGroup
  const isLiteUser = isNewPremiumUser && userActiveLicense?.licenseType === LicenseType.LICENSE_PREMIUM_LITE
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)

  const handlePopoverOpen = event => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const onClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setAnchorEl(null)
  }

  const handleSendButtonClick = e => {
    if ((!isLiteUser)) {
      hrefFunction()
    } else {
      if (template && template.id === 99999) {
        hrefFunction()
      } else {
        handlePopoverOpen(e)
      }
    }
  }

  const dispatch = useDispatch()
  const scopes = useIsScope({
    templates: [PermissionScope.SCOPE_CAN_USE_TEMPLATES],
  })

  const formOpen = updateOpen || createOpen

  const isDesktop = is.desktop()

  useEffect(() => {
    switchTemplate(null)
    switchLanguage(mapLanguage(initialLanguage))
  }, [open, initialLanguage])

  useEffect(() => {
    try {
      if (template && language) {
          if ((isLiteUser) && template.id !== 99999) {
            setEnabled(false)
          } else {
            loadLanguage(language).then(() => {
              const [emailTitle, emailContent] = [
                template.title,
                template.content,
              ].map(mapLabels(language))
              setTitle(decodeEmojiFromBackend(emailTitle ?? ''))
              setContent(decodeEmojiFromBackend(emailContent ?? ''))
              setEnabled(true)
            })
          }
      } else {
        setEnabled(false)
      }
    } catch (e) {
      console.log(e)

      store.dispatch(fireToastError(t('errStatus_0a')))
      setEnabled(false)
    }
  }, [template, language, mapLabels, t, userActiveLicense,userPriceGroup, isLiteUser])

  function closeModal() {
    close()
  }

  function onConfigIconClick(e) {
    if (scopes.templates) {
      if (userActiveLicense?.licenseType === LicenseType.LICENSE_PREMIUM_LITE && userPriceGroup === 3) {
        if (isMobile) {
          setIsModalOpened(true)
        } else {
          handlePopoverOpen(e)
        }
      } else {
        window.open(`/templates`, '_blank')
      }
    } else {
      Mixpanel.track('Employee Access Denied', {
        'Employee ID': String(employee),
      })
      dispatch(fireToastError(t('BED_3621_no_permission')))
    }
  }

  function selectTemplateInAnotherLanguage(
    currentTemplate: ITemplate,
    language: CountryCode,
  ) {
    if (currentTemplate && currentTemplate.idType === 99999) {
      return { ...currentTemplate, title: mapCodeToTemplate(language) }
    }
    const nextTemplate = currentTemplate
      ? templates.find(
        t => t.idType === currentTemplate.idType && t.language === language,
      )
      : null

    return nextTemplate || null
  }

  function handleTemplateSelectChange(templateId: ITemplate['id']) {
    switchTemplate(
      templateId === 99999
        ? (({
          content: '',
          id: 99999,
          idType: 99999,
          title: mapCodeToTemplate(language),
        } as unknown) as ITemplate)
        : templates.find(t => t.id === templateId),
    )
  }

  const mapTemplateIdToEventString = (id: number) => {
    switch (id) {
      case 1:
        return 'Request for payment'
      case 2:
        return 'Reminder'
      case 3:
        return 'Reservation cancelled'
      case 4:
        return 'Confirmation of the payment'
      case 5:
        return 'Confirmation of the payment of the full amount'
      case 6:
        return 'Thank you for stay'
      case 99999:
        return 'Regular message'
    }
  }

  function hrefFunction() {
    if (type === 'sms') {
      if (formOpen) {
        Mixpanel.track('Reservation Form Send A SMS Completed', {
          'Reservation ID': Number(event.id),
          'SMS Message Template': mapTemplateIdToEventString(template.idType),
        })
        Mixpanel.people.increment(userId, 'SMS Messages Sent', 1)
      }
      return close()
    }

    Mixpanel.people.increment(userId, 'Email Messages Sent', 1)
    if (formOpen) {
      Mixpanel.track('Reservation Form Send An Email Completed', {
        'Reservation ID': Number(event.id),
        'Email Message Template': mapTemplateIdToEventString(template.idType),
      })
    } else {
      if (employee) {
        Mixpanel.track('Employees List Send An Email ', {
          'Employee ID': employee ? String(employee) : undefined,
        })
      } else {
        Mixpanel.track(
          tab === 'Reservation List Latest Tab Used'
            ? 'Reservation List Latest Send An Email Completed'
            : 'Reservation List Cancelled Send An Email Completed',
          {
            'Reservation ID': Number(event.id),
            'Email Message Template': mapTemplateIdToEventString(
              template.idType,
            ),
            'Reservation Down Payment Time To Pay Days': (event as IEvent)
              .dateToPay
              ? Math.abs(
                differenceInCalendarDays(
                  new Date(),
                  (event as IEvent).dateToPay,
                ),
              )
              : undefined,
            'Reservation Down Payment Time To Pay Date': !!(event as IEvent)
              .dateToPay,
            'Reservation Down Payment Time To Pay Days Reminder': (event as IEvent)
              .dateToPayAfterStart
              ? Math.abs(
                differenceInCalendarDays(
                  new Date(),
                  (event as IEvent).dateToPayAfterStart,
                ),
              )
              : undefined,
            'Reservation Down Payment Time To Pay Date Reminder': !!(event as IEvent)
              .dateToPayAfterStart,
            'Reservation Person Phone Length':
              (event as IEvent).guest && event.guest.phone
                ? Number(event.guest.phone.length)
                : undefined,
            'Reservation Person Email Length':
              (event as IEvent).guest && event.guest.email
                ? Number(event.guest.email.length)
                : undefined,
          },
        )
      }
    }
  }

  return (
    <>
      <ModalFlex open={open} onClose={closeModal} key={email}>
        <TemplateSendModalWrapper>
          <ModalHeader>
            <Flex style={{ minWidth: 0, width: '100%' }} justifyStart={true}>
              <ModalHeaderLabel
                style={{
                  fontSize: `${16 * size}px`,
                  padding: `${5 * size}px 0px`,
                }}
              >
                <Flex row={true} style={{ minWidth: 0 }}>
                  <Flex column={true} style={{ minWidth: 0 }}>
                    <Trans i18nKey={'contact_send_message'}>
                      Wyślij wiadomość
                    </Trans>
                    <Flex style={{ minWidth: 0 }}>
                      <span
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {email}
                      </span>
                    </Flex>
                  </Flex>
                </Flex>
              </ModalHeaderLabel>
            </Flex>
            <Flex
              //Weird padding and margin to align it with rest of elements
              style={{ paddingLeft: `${15 * size}px`, width: '20%' }}
              justifyEnd={true}
              alignCenter={true}
            >
              <Icon
                style={{ marginRight: `${-4 * size}px` }}
                onClick={closeModal}
              />
            </Flex>
          </ModalHeader>
          <Flex style={{ width: '100%' }} column={true}>
            <Select.Field
              size={size}
              label={t('custom_messages_template')}
              width={'100%'}
              menuPlacement={'auto'}
              menuPosition={'fixed'}
              value={template ? template.id : null}
              noOptionsMessage={t('search_not_found')}
              onChange={handleTemplateSelectChange}
              placeholder={t('select')}
              wrapperFull={true}
              withConfig={true}
              onIconClick={onConfigIconClick}
              configIconStyle={{
                color: (isLiteUser  || (template && !sendEnabled)) ? '#C2C2C2' : undefined,
                right: `${5 * size}px`,
                top: `${13 * size}px`,
              }}
            >
              <Select.Option value={99999} key={99999}>
                {mapCodeToTemplate(language)}
              </Select.Option>

              {templates
                .filter(t => t.language === language)
                .map(template => (
                  <Select.Option value={template.id} key={template.id}>
                    <Flex style={{gap: '8px', alignItems: 'center'}}>
                      {template.typeTitle}
                      {userActiveLicense?.licenseType === LicenseType.LICENSE_PREMIUM_LITE && userPriceGroup === 3 && <PopoveredIcon2 />}
                    </Flex>
                  </Select.Option>
                ))}
            </Select.Field>

            <Select.Field
              size={size}
              label={t('email_language_selection_title')}
              width={'100%'}
              value={language}
              placeholder={t('select')}
              noOptionsMessage={t('search_not_found')}
              onChange={v => {
                switchLanguage(v)
                switchTemplate(selectTemplateInAnotherLanguage(template, v))
              }}
              wrapperStyle={{ marginTop: `${15 * size}px` }}
            >
              {countryCodes
                .map(code => (code === 'pl' ? 'pl_PL' : code)) //Templates have pl_PL code instead of normal
                .map(code => (
                  <Select.Option
                    value={code}
                    key={code}
                    icon={
                      <Flag
                        countryCode={
                          asCountryCode(code).toLowerCase() as CountryCode
                        }
                        width={16}
                        height={16}
                      />
                    }
                  >
                    {mapCodeToLanguage(code)}
                  </Select.Option>
                ))}
            </Select.Field>
          </Flex>

          <Divider style={{ margin: `${15 * size}px 0px` }} />
          <Flex full={true} row={true}>
            <Button
              size={size}
              style={{ height: `${38 * size}px`, width: '100%' }}
              primary={template && sendEnabled}
              href={`${type}:${email}${issafari && type === 'sms' ? '&' : '?'}${
                type === 'mailto' ? `subject=${title}&` : ''
              }body=${encodeURIComponent(
                isDesktop || type === 'sms'
                  ? content.replace(new RegExp('<br />', 'g'), '\r\n')
                  : content,
              )}`}
              onClick={e => handleSendButtonClick(e)}
              disabled={template && !sendEnabled}
            >
              {template && !sendEnabled ? <div style={{ marginRight: `${5 * size}px` }}><PopoveredIcon2 variant={'white'}/></div> : <FiSave style={{ marginRight: `${5 * size}px` }} />}
              <Trans i18nKey={'send'}>Wyślij</Trans>
            </Button>
          </Flex>
        </TemplateSendModalWrapper>
      </ModalFlex>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={onClose}
      >
      <UpgradePremiumPopover handleClose={onClose} />
      </Popover>
      <UpgradePremiumBlockScreenModal
        open={isModalOpened}
        close={() => setIsModalOpened(false)}
      />
    </>
  )
}

export default TemplateSendModal
